import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { UserContext } from "../../providers/UserProvider";
import { authLogin, updateToken, userInfo, forgotPassword as forgotPasswordApi, resetPassword as resetPasswordApi } from "../../app/api";
import { ExceptionMap } from "antd/es/result";
import { Link, redirect, useNavigate } from "react-router-dom";
import EeljCountdown from "../eelj/EeljCountdown";

const Login = () => {
  const navigate = useNavigate();
  const {userBio, setUserBio, handleLogin, eeljuud} = useContext(UserContext);
  const { t } = useTranslation();

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");

  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>("");

  const [verifyOTP, setVerifyOTP] = useState(false);
  const [tokenOTP, setTokenOTP] = useState("");
  const [resetPasswordError, setResetPasswordError] = useState("");
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordVerify, setResetPasswordVerify] = useState("");

  const [error, setError] = useState(false);

  const Login = useCallback(async () => {
    setError(false);
    try {
      const loggedIn = await handleLogin(phone, password);
      navigate('/');
    } catch(err) {
      setError(true);
    }
  }, [phone, password]);

  const requestOTP = useCallback(async () => {
    setForgotPasswordError('');
    try {
      const resp = await forgotPasswordApi({email: forgotPasswordEmail});
      setVerifyOTP(true);
    } catch(err) {
      setForgotPasswordError(t('login.forgotUserNotFound'));
    }
  }, [forgotPasswordEmail]);

  const handleResetPassword = useCallback(async () => {
    setResetPasswordError('');
    setResetPasswordSuccess(false);

    if(resetPassword !== resetPasswordVerify) {
      setResetPasswordError(t('login.forgotPasswordMismatch'));
      return;
    }

    try {
      const resp = await resetPasswordApi(
        {
          password: resetPassword,
          password_confirmation: resetPasswordVerify,
        },
        tokenOTP
      );

      setResetPasswordSuccess(true);
    } catch(err: any) {
      setResetPasswordError(t('login.forgotOtpError'));
    }
  }, [resetPassword, resetPasswordVerify, tokenOTP]);

  return (
    <div className="relative min-h-screen pt-40 pb-20 sm:pt-48 sm:pb-28 px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center bg-[#0a1628] -mt-20 overflow-hidden">
      {/* Gradient mesh orbs */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-primary-600/20 blur-[120px]" />
        <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-secondary-500/15 blur-[120px]" />
        <div className="absolute -bottom-20 right-1/3 w-[400px] h-[400px] rounded-full bg-primary-400/10 blur-[100px]" />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative w-full max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left Side - Welcome */}
          <div className="text-center lg:text-left">
            {eeljuud.activeEelj[0] ? (
              <div className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-3xl p-8">
                <EeljCountdown {...eeljuud.activeEelj[0]}/>
              </div>
            ) : (
              <div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                  <span className="text-primary-400">{t('login.welcome1')}</span>{` ${t('login.welcome2')}`}
                </h1>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6">
                  {t('login.welcome3')}
                </h2>
                <p className="text-lg text-slate-400 leading-relaxed">
                  {t('login.welcomeDesc')}
                </p>
              </div>
            )}
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full">
            <div className="bg-white/5 border border-white/10 backdrop-blur-sm rounded-3xl p-8 sm:p-10 md:p-12">
              <div className="mb-8">
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
                  {t('login.title')}
                </h2>
                <p className="text-slate-400">
                  {t('login.subtitle')}
                </p>
              </div>

              <form>
                  {
                    forgotPassword ?
                    <>
                    {
                      verifyOTP ?
                      <>
                        {resetPasswordError && (
                          <div
                            className="mb-6 p-4 rounded-md bg-danger-50/10 border border-danger-500/30 text-danger-300"
                            role="alert"
                          >
                            <span className="block sm:inline">
                              {resetPasswordError}
                            </span>
                          </div>
                        )}
                        {resetPasswordSuccess && (
                          <div
                            className="mb-6 p-4 rounded-md bg-primary-500/10 border border-primary-500/30 text-primary-300 cursor-pointer hover:bg-primary-500/20 transition-colors"
                            role="alert"
                            onClick={() => {
                              setForgotPassword(false);
                              setVerifyOTP(false);
                            }}
                          >
                            <span className="block sm:inline underline">
                              {t('login.forgotResetSuccess')}
                            </span>
                          </div>
                        )}
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            {t('login.forgotOtpLabel')}
                          </label>
                          <input
                            value={tokenOTP}
                            type="text"
                            className="w-full px-5 py-3.5 rounded-2xl border-2 border-white/10 bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200"
                            placeholder={t('login.forgotOtpPlaceholder')}
                            onChange={(e) => setTokenOTP(e.target.value)}
                          />
                        </div>
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            {t('login.forgotNewPassword')}
                          </label>
                          <input
                            value={resetPassword}
                            type="password"
                            className="w-full px-5 py-3.5 rounded-2xl border-2 border-white/10 bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200 text-center text-2xl tracking-widest font-bold"
                            placeholder="****"
                            minLength={4}
                            maxLength={4}
                            onChange={(e) => setResetPassword(e.target.value)}
                          />
                        </div>
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            {t('login.forgotConfirmPassword')}
                          </label>
                          <input
                            value={resetPasswordVerify}
                            type="password"
                            className="w-full px-5 py-3.5 rounded-2xl border-2 border-white/10 bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200 text-center text-2xl tracking-widest font-bold"
                            placeholder="****"
                            minLength={4}
                            maxLength={4}
                            onChange={(e) => setResetPasswordVerify(e.target.value)}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {e.preventDefault(); handleResetPassword();}}
                          className="w-full mb-4 px-8 py-3.5 rounded-full font-bold text-white bg-gradient-to-r from-primary-600 to-secondary-500 shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-secondary-500/30 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-secondary-300 transition-all duration-200"
                        >
                          {t('login.forgotResetBtn')}
                        </button>
                      </>
                      :
                      <>
                        {forgotPasswordError && (
                          <div
                            className="mb-6 p-4 rounded-md bg-danger-50/10 border border-danger-500/30 text-danger-300"
                            role="alert"
                          >
                            <span className="block sm:inline">
                              {forgotPasswordError}
                            </span>
                          </div>
                        )}
                        <div className="mb-6">
                          <label className="block text-sm font-medium text-slate-300 mb-2">
                            {t('login.forgotEmailLabel')}
                          </label>
                          <input
                            value={forgotPasswordEmail}
                            type="email"
                            className="w-full px-5 py-3.5 rounded-2xl border-2 border-white/10 bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200"
                            placeholder={t('login.forgotEmailPlaceholder')}
                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {e.preventDefault(); requestOTP();}}
                          className="w-full mb-4 px-8 py-3.5 rounded-full font-bold text-white bg-gradient-to-r from-primary-600 to-secondary-500 shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-secondary-500/30 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-secondary-300 transition-all duration-200"
                        >
                          {t('login.forgotRequestCode')}
                        </button>
                      </>
                    }
                      <button
                        type="button"
                        className="w-full text-center text-sm text-primary-400 hover:text-primary-300 font-medium transition-colors"
                        onClick={() => {
                          setForgotPassword(false);
                          setVerifyOTP(false);
                        }}
                      >
                        {t('login.back')}
                      </button>
                    </>
                    :
                    <>
                      {error && (
                        <div
                          className="mb-6 p-4 rounded-md bg-danger-50/10 border border-danger-500/30 text-danger-300"
                          role="alert"
                        >
                          <span className="block sm:inline">
                            {t('login.errorCredentials')}
                          </span>
                        </div>
                      )}

                      <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          {t('login.phone')}
                        </label>
                        <input
                          value={phone}
                          type="text"
                          className="w-full px-5 py-3.5 rounded-2xl border-2 border-white/10 bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200"
                          placeholder={t('login.phonePlaceholder')}
                          onChange={(e) => setPhone(e.target.value)}
                        />
                      </div>

                      <div className="mb-6">
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                          {t('login.password')}
                        </label>
                        <input
                          value={password}
                          type="password"
                          className="w-full px-5 py-3.5 rounded-2xl border-2 border-white/10 bg-white/5 text-white placeholder-slate-500 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-400 transition-all duration-200 text-center text-2xl tracking-widest font-bold"
                          placeholder="****"
                          onChange={(e) => setPassword(e.target.value)}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {e.preventDefault(); Login();}}
                        className="w-full mb-4 px-8 py-3.5 rounded-full font-bold text-white bg-gradient-to-r from-primary-600 to-secondary-500 shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-secondary-500/30 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-secondary-300 transition-all duration-200"
                      >
                        {t('login.loginBtn')}
                      </button>

                      <div className="mb-4 text-center">
                        <span className="text-slate-500 text-sm">{t('login.or')}</span>
                      </div>

                      <Link to={'/register'}>
                        <button
                          type="button"
                          className="rounded-full font-bold text-white border-2 border-white/20 bg-white/5 hover:bg-white/10 px-8 py-3.5 w-full mb-4 transition-colors"
                        >
                          {t('login.registerBtn')}
                        </button>
                      </Link>

                      <button
                        type="button"
                        className="w-full text-center text-sm text-primary-400 hover:text-primary-300 font-medium transition-colors"
                        onClick={() => setForgotPassword(true)}
                      >
                        {t('login.forgotPassword')}
                      </button>
                    </>
                  }
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;